import { useEffect, useRef, useState } from "react";
import { COLORS, Ic, ICONS, Mark } from "@/lib/theme";
import { streamAI, apiGet } from "@/lib/api";

const STARTERS = [
  "What time is Mincha today?",
  "How do I say Kaddish for a parent?",
  "What is the significance of the Amidah?",
  "Tell me about Rosh Chodesh customs",
];

function formatText(text) {
  // very light markdown rendering
  const lines = text.split("\n");
  return lines.map((line, i) => {
    // bold
    const html = line
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`(.+?)`/g, "<code>$1</code>");
    return (
      <p key={i} dangerouslySetInnerHTML={{ __html: html || "&nbsp;" }} style={{ margin: "4px 0" }} />
    );
  });
}

export default function AI({ member, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [health, setHealth] = useState(null);
  const listRef = useRef(null);

  useEffect(() => { apiGet("/health").then(setHealth).catch(() => setHealth({ ai_configured: false })); }, []);

  useEffect(() => {
    // Load session from localStorage if member exists
    const key = member ? `ls_ai_session_${member.id}` : "ls_ai_session_guest";
    const saved = localStorage.getItem(key);
    if (saved) setSessionId(saved);
  }, [member]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);

    let assistantIndex;
    setMessages((m) => { assistantIndex = m.length - 1; return m; });

    await streamAI({
      sessionId,
      message: content,
      onSessionId: (sid) => {
        if (!sessionId) {
          setSessionId(sid);
          const key = member ? `ls_ai_session_${member.id}` : "ls_ai_session_guest";
          localStorage.setItem(key, sid);
        }
      },
      onDelta: (delta) => {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content + delta };
          return copy;
        });
      },
      onError: (err) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: `⚠️ ${err}` };
          return copy;
        });
      },
      onDone: () => setStreaming(false),
    });
    setStreaming(false);
  };

  return (
    <div className="anim-slideIn" data-testid="ai-screen" style={{ position: "absolute", inset: 0, background: COLORS.cream, zIndex: 40, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div className="ls-header" style={{ padding: "16px 22px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            data-testid="ai-close"
            onClick={onClose}
            aria-label="Close"
            className="no-tap-highlight"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <Ic d={ICONS.back} color="#fff" size={26} />
          </button>
          <div style={{ flex: 1 }}>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 20, color: "#fff" }}>LinkShul AI Assistant</div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 700 }}>Powered by Claude · Approved Orthodox sources</div>
            {health && <div style={{ color: health.ai_configured ? "#d8ffd9" : "#ffd2d2", fontSize: 11, fontWeight: 800, marginTop: 3 }}>AI STATUS: {health.ai_configured ? "CONNECTED / CONFIGURED" : "NOT CONFIGURED"}</div>}
          </div>
          <Mark size={32} tone="white" />
        </div>
      </div>

      {/* Message list */}
      <div ref={listRef} className="ls-scroll" style={{ flex: 1, padding: "16px 18px 12px", overflowY: "auto" }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <div style={{ display: "inline-block" }}>
              <Mark size={72} />
            </div>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 20, color: COLORS.ink, marginTop: 8 }}>Ask me anything</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13, marginTop: 4, padding: "0 20px", lineHeight: 1.5 }}>
              I can answer questions about Judaism, Torah, halacha, LinkShul services, or community life — drawing from approved Orthodox sources including Sefaria.org.
            </div>
            <div style={{ display: "grid", gap: 8, marginTop: 22 }}>
              {STARTERS.map((s) => (
                <button
                  key={s}
                  data-testid={`ai-starter-${STARTERS.indexOf(s)}`}
                  onClick={() => send(s)}
                  className="no-tap-highlight"
                  style={{
                    background: "#fff",
                    border: "1px solid " + COLORS.border,
                    borderRadius: 14,
                    padding: "12px 14px",
                    color: COLORS.ink,
                    fontWeight: 700,
                    fontSize: 13.5,
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "'Nunito', sans-serif",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 10 }}>
            <div
              className={m.role === "assistant" ? "ai-msg" : ""}
              data-testid={`ai-message-${i}`}
              style={{
                maxWidth: "82%",
                padding: "10px 14px",
                borderRadius: 16,
                background: m.role === "user" ? COLORS.lilac : "#fff",
                color: COLORS.ink,
                fontWeight: 600,
                fontSize: 14,
                lineHeight: 1.5,
                boxShadow: m.role === "assistant" ? "0 4px 12px rgba(91,42,130,0.08)" : "none",
                border: m.role === "assistant" ? "1px solid " + COLORS.border : "none",
                borderBottomRightRadius: m.role === "user" ? 4 : 16,
                borderBottomLeftRadius: m.role === "assistant" ? 4 : 16,
              }}
            >
              {m.role === "assistant" ? formatText(m.content || (streaming ? "…" : "")) : m.content}
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <div style={{ padding: "10px 14px 16px", background: COLORS.cream, borderTop: "1px solid " + COLORS.border }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Ask a question…"
            className="ls-input"
            style={{ flex: 1 }}
            disabled={streaming}
          />
          <button
            data-testid="ai-send"
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            className="no-tap-highlight"
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              border: "none",
              background: streaming || !input.trim() ? "#c4aed4" : `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.plum})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: streaming || !input.trim() ? "default" : "pointer",
              flexShrink: 0,
              boxShadow: "0 6px 14px rgba(91,42,130,0.3)",
            }}
          >
            <Ic d={ICONS.send} color="#fff" size={20} w={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
